import { useCallback, useMemo, useRef, useState } from "react";
import useHaklaiTableValidationController from "../../shared/table/useHaklaiTableValidationController";

// Property-parts controller: thin wrapper around the shared validation
// controller. Adds the property-parts-only concerns on top:
//   - Add-menu anchor state for the B+G "+" menu,
//     with focus-restoration helpers for the MUI Menu interaction.
//   - `valueInputRefs` / `builtValueInputRefs` — column-specific DOM ref maps
//     the section uses to focus a newly-added row's first input.
//   - A deferred-add timer contract is intentionally NOT here; that lives in
//     the section itself because it's tied to menu-close timing.
// Every other concern (committed/touched state, scroll/flash, rowRefs, lock
// blur, sticky header) comes from the shared hook and behaves identically in
// both tables.

const useHaklaiPropertyPartsController = ({
  expanded,
  locked,
  parts
}) => {
  // Memoize the row-key list so the shared hook's prune effect doesn't fire
  // on every render. `parts` is already a memoized object upstream, but
  // `Object.keys` returns a fresh array each call.
  const rowKeys = useMemo(() => Object.keys(parts || {}), [parts]);

  const base = useHaklaiTableValidationController({
    rowKeys,
    locked,
    expanded,
    resetValidationOnRowKeyRemoval: true
  });

  const [sectionBgAddMenuAnchorEl, setSectionBgAddMenuAnchorEl] = useState(null);
  const valueInputRefs = useRef({});
  const builtValueInputRefs = useRef({});

  const blurMenuFocusIfNeeded = useCallback(() => {
    const activeElement = document.activeElement;
    if (!activeElement || activeElement === document.body) return;
    const isFocusInsideMenu = Boolean(
      activeElement.closest?.(".MuiMenu-paper, .MuiPopover-paper, .MuiPopover-root, [role='menu']")
    );
    if (!isFocusInsideMenu) return;
    if (typeof activeElement.blur === "function") activeElement.blur();
  }, []);

  // Returning focus to the anchor element after an MUI Menu closes requires
  // both a `requestAnimationFrame` and a `setTimeout(0)` pass:
  //   - rAF runs before the next paint, catching the normal path when the
  //     Menu's own "restore focus on unmount" hook has already released the
  //     document's active element.
  //   - setTimeout(0) runs on the next task after any `setTimeout(0)` that
  //     MUI itself schedules internally. Without it, MUI's post-close
  //     focus reset can overwrite our anchor restoration.
  // Calling `focus()` twice on the same element is idempotent, and each
  // call guards against the element being removed from the DOM.
  const restoreAnchorFocusAfterMenuClose = useCallback((anchorEl) => {
    if (!anchorEl || typeof anchorEl.focus !== "function") return;
    const restoreFocus = () => { if (document.contains(anchorEl)) anchorEl.focus(); };
    window.requestAnimationFrame(restoreFocus);
    window.setTimeout(restoreFocus, 0);
  }, []);

  const setValueInputRef = useCallback((key) => (input) => {
    if (input) { valueInputRefs.current[key] = input; return; }
    delete valueInputRefs.current[key];
  }, []);

  const setBuiltValueInputRef = useCallback((key) => (input) => {
    if (input) { builtValueInputRefs.current[key] = input; return; }
    delete builtValueInputRefs.current[key];
  }, []);

  const openSectionBgAddMenuAnchor = useCallback((anchorEl) => setSectionBgAddMenuAnchorEl(anchorEl), []);

  const closeSectionBgAddMenu = useCallback(() => {
    const anchorEl = sectionBgAddMenuAnchorEl;
    blurMenuFocusIfNeeded();
    setSectionBgAddMenuAnchorEl(null);
    restoreAnchorFocusAfterMenuClose(anchorEl);
  }, [blurMenuFocusIfNeeded, restoreAnchorFocusAfterMenuClose, sectionBgAddMenuAnchorEl]);

  return {
    ...base,
    sectionBgAddMenuAnchorEl,
    setValueInputRef,
    setBuiltValueInputRef,
    openSectionBgAddMenuAnchor,
    closeSectionBgAddMenu
  };
};

export default useHaklaiPropertyPartsController;
